import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { AuthContext } from '../../auth/AuthContext';
import { getToken } from '../../auth/authStorage';
import './ChatWidget.css';

const API_BASE = process.env.REACT_APP_API_BASE || '';
// const AUTO_OPEN_SHOWN_KEY = 'dyna_chat_auto_shown'; // auto-open disabled — see below

// Convert simple markdown to safe HTML.
// Sanitizes HTML first, then applies bold + link transforms.
function renderMarkdown(text) {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Only render links pointing to Dynalog-owned domains (or the WhatsApp/email
  // contact hand-off) — anything else becomes plain text.
  const ALLOWED_DOMAINS = /^https?:\/\/(stage\.imageonline\.co\.in|dynalogindia\.com|www\.dynalogindia\.com|wa\.me)(\/|$)/i;
  const ALLOWED_MAILTO = /^mailto:[^@]+@dynalogindia\.com$/i;

  let html = escaped
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+|mailto:[^)]+)\)/g, (_, label, url) => {
      const isAllowed = url.startsWith('mailto:') ? ALLOWED_MAILTO.test(url) : ALLOWED_DOMAINS.test(url);
      if (!isAllowed) return label;  // hallucinated / off-domain → label only
      // Tag product/browse links so we can add a gap AFTER them (separates products).
      const cls = /view product|browse/i.test(label) ? ' class="dyna-chat-product-link"' : '';
      return `<a href="${url}"${cls} target="_blank" rel="noopener noreferrer">${label} ↗</a>`;
    })
    .replace(/\n{2,}/g, '\n')
    .replace(/\n/g, '<br/>');
  // The product link is a block with bottom-margin (CSS) — drop the <br/> right
  // after it so we get ONE clean gap after "View Product", not a double space.
  html = html.replace(/(<a href="[^"]*" class="dyna-chat-product-link"[^>]*>.*?<\/a>)<br\/>/g, '$1');
  return html;
}

// SVG Icons
// chatbot.svg is a full self-contained icon (its own circular gradient
// background + glyph) — it fills the launcher button entirely rather than
// sitting as a small glyph on top of the button's own background.
const IconChat = () => (
  <img
    src={`${process.env.PUBLIC_URL}/assets/images/icons/chatbot.svg`}
    alt=""
    width="58"
    height="58"
  />
);

const IconClose = ({ color = 'white', size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6l12 12" stroke={color} strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
);

const IconSend = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
    <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" stroke="white" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BotAvatar = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" fill="white"/>
  </svg>
);

export default function ChatWidget() {
  const [isOpen, setIsOpen]           = useState(false);
  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState('');
  const [isTyping, setIsTyping]       = useState(false);
  const [isFrozen, setIsFrozen]       = useState(false);
  // sessionId is persisted in localStorage so it survives page reloads
  const [sessionId, setSessionId]     = useState(() => localStorage.getItem('dyna_chat_session') || '');
  const [initialized, setInitialized] = useState(false);
  // Guards the "fetch greeting on first open" effect until we know whether
  // there's restorable history for the stored session — prevents it firing
  // (and posting a throwaway __init__) while the restore fetch is in flight.
  const [historyChecked, setHistoryChecked] = useState(false);

  const messagesEndRef  = useRef(null);
  const lastBotMsgRef   = useRef(null);
  const inputRef        = useRef(null);
  // const hasInteractedRef = useRef(false); // auto-open disabled — see below

  // Auth token: AuthProvider stores it in localStorage
  const authToken = useSelector(state => state?.auth?.token) || getToken() || '';

  // Name from AuthContext (React state) — always up to date when user is logged in
  const { user: authUser } = useContext(AuthContext);
  const clientUserName = authUser?.first_name || '';

  const getHeaders = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (authToken) h['Authorization'] = `Bearer ${authToken}`;
    return h;
  }, [authToken]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Scroll to top of new bot reply so the user reads from the beginning.
  // For user messages and typing indicator, keep scrolling to the bottom.
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.role === 'assistant' && lastBotMsgRef.current) {
      lastBotMsgRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isTyping) scrollToBottom();
  }, [isTyping, scrollToBottom]);

  // Allow external elements (e.g. chatboat.svg button) to open the widget
  useEffect(() => {
    const handler = () => setIsOpen(true);
    document.addEventListener('open-chat', handler);
    return () => document.removeEventListener('open-chat', handler);
  }, []);

  // On logout, the conversation may carry the now-logged-out user's name/email/
  // phone (from a submitted inquiry) — drop it so the next person on this device
  // doesn't inherit it. AuthProvider dispatches this after clearing auth state.
  useEffect(() => {
    const handler = () => {
      setMessages([]);
      setSessionId('');
      localStorage.removeItem('dyna_chat_session');
      setIsFrozen(false);
      setInitialized(false);
      // No session left to restore — let the next open fetch a fresh greeting directly.
      setHistoryChecked(true);
    };
    document.addEventListener('dyna-chat-logout', handler);
    return () => document.removeEventListener('dyna-chat-logout', handler);
  }, []);

  // Auto-open disabled (was: open the widget after 10s if the user hadn't
  // clicked the launcher yet, once per browser session). Uncomment to
  // re-enable — also uncomment AUTO_OPEN_SHOWN_KEY and hasInteractedRef above,
  // and the two lines marked below in the launcher's onClick.
  // useEffect(() => {
  //   if (sessionStorage.getItem(AUTO_OPEN_SHOWN_KEY)) return;
  //   const timer = setTimeout(() => {
  //     if (!hasInteractedRef.current) {
  //       setIsOpen(true);
  //       sessionStorage.setItem(AUTO_OPEN_SHOWN_KEY, '1');
  //     }
  //   }, 10000);
  //   return () => clearTimeout(timer);
  // }, []);

  // On mount: if a chat session survived a page refresh (persisted in
  // localStorage), pull its history back from the API so reopening the
  // widget resumes the conversation instead of starting over.
  useEffect(() => {
    const storedSession = localStorage.getItem('dyna_chat_session') || '';
    if (!storedSession) {
      setHistoryChecked(true);
      return;
    }

    (async () => {
      try {
        const res  = await fetch(`${API_BASE}/chat/session/${storedSession}`, { headers: getHeaders() });
        const data = await res.json();
        if (Array.isArray(data.messages) && data.messages.length > 0) {
          setMessages(data.messages);
          setSessionId(storedSession);
          const last = data.messages[data.messages.length - 1];
          if (last?.role === 'assistant' && last.content.startsWith('✅')) {
            setIsFrozen(true);
          }
          setInitialized(true);
        }
      } catch {
        // API unreachable — fall through to the normal fresh-greeting flow on open
      } finally {
        setHistoryChecked(true);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On open: fetch greeting on first open only if there's no restored history;
  // subsequent open/close reuses React state
  useEffect(() => {
    if (!isOpen || initialized || !historyChecked) {
      if (isOpen) setTimeout(() => inputRef.current?.focus(), 280);
      return;
    }
    setInitialized(true);
    fetchGreeting();
  }, [isOpen, historyChecked]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchGreeting() {
    // Read both fresh at call-time to avoid stale closure
    const userName       = authUser?.first_name || '';
    const storedSession  = localStorage.getItem('dyna_chat_session') || '';
    setIsTyping(true);
    try {
      const res  = await fetch(`${API_BASE}/chat/message`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ message: '__init__', sessionId: storedSession, userName }),
      });
      const data = await res.json();
      if (data.sessionId) {
        setSessionId(data.sessionId);
        localStorage.setItem('dyna_chat_session', data.sessionId);
      }
      if (data.reply) {
        setMessages([{ role: 'assistant', content: data.reply }]);
      }
    } catch {
      setMessages([{ role: 'assistant', content: 'Hi! How can I help you today? 👋' }]);
    } finally {
      setIsTyping(false);
    }
  }

  // `textArg` lets an option button send its label directly; otherwise use the input box.
  async function sendMessage(textArg) {
    const text = (typeof textArg === 'string' ? textArg : input).trim();
    if (!text || isTyping || isFrozen) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setIsTyping(true);

    let didFreeze = false;
    try {
      const res  = await fetch(`${API_BASE}/chat/message`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ message: text, sessionId }),
      });
      const data = await res.json();

      if (data.sessionId) {
        setSessionId(data.sessionId);
        localStorage.setItem('dyna_chat_session', data.sessionId);
      }
      if (data.reply) {
        // `options` (if any) render as clickable selection buttons under this reply.
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.reply,
          options: Array.isArray(data.options) ? data.options : [],
        }]);
        if (data.reply.startsWith('✅')) {
          setIsFrozen(true);
          didFreeze = true;
        }
      } else if (data.error) {
        // includes the cooldown message ("…try again in about N minutes") on a 429.
        setMessages(prev => [...prev, { role: 'assistant', content: `${data.error} 🙏` }]);
      }
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Sorry, something went wrong. Please try again. 🙏' },
      ]);
    } finally {
      setIsTyping(false);
      if (!didFreeze) {
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    }
  }

  function startNewConversation() {
    setMessages([]);
    setSessionId('');
    localStorage.removeItem('dyna_chat_session');
    setIsFrozen(false);
    setInitialized(false);
    fetchGreeting();
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function handleClose() {
    setIsOpen(false);
  }

  return (
    <>
      {/* ── Floating launcher button ── */}
      <button
        className={`dyna-chat-launcher${isOpen ? ' open' : ''}`}
        onClick={() => {
          // hasInteractedRef.current = true; // auto-open disabled — see above
          // sessionStorage.setItem(AUTO_OPEN_SHOWN_KEY, '1');
          setIsOpen(v => !v);
        }}
        aria-label="Chat with DIA"
        title="Chat with DIA"
      >
        {isOpen ? <IconClose /> : <IconChat />}
      </button>

      {/* ── Chat panel ── */}
      {isOpen && (
        <div
          className={`dyna-chat-panel${messages.some(m => m.role === 'user') ? ' expanded' : ''}`}
          role="dialog"
          aria-label="Dynalog Chat"
        >

          {/* Header */}
          <div className="dyna-chat-header">
            <div className="dyna-chat-header-avatar">
              <BotAvatar size={22} />
            </div>
            <div className="dyna-chat-header-info">
              <span className="dyna-chat-header-name">DIA</span>
              <span className="dyna-chat-header-sub">
                <span className="dyna-chat-online-dot" />
                Dynalog's Intelligent Assistant
              </span>
            </div>
            <button className="dyna-chat-header-close" onClick={handleClose} aria-label="Close chat">
              <IconClose size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="dyna-chat-messages">
            {messages.map((msg, i) => {
              const isLastBot   = msg.role === 'assistant' && i === messages.length - 1;
              const showOptions = isLastBot && Array.isArray(msg.options) && msg.options.length > 0 && !isFrozen;
              return (
                <div
                  key={i}
                  ref={isLastBot ? lastBotMsgRef : null}
                  className={`dyna-chat-msg ${msg.role}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="dyna-chat-bot-avatar">
                      <BotAvatar size={14} />
                    </div>
                  )}
                  <div className="dyna-chat-bubble">
                    <div
                      className="dyna-chat-bubble-text"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                    />
                    {/* Selection options — stacked clickable rows INSIDE the bubble */}
                    {showOptions && (
                      <div className="dyna-chat-options">
                        {msg.options.map((opt, k) => (
                          <button
                            key={k}
                            type="button"
                            className="dyna-chat-option-row"
                            disabled={isTyping}
                            onClick={() => sendMessage(opt)}
                          >
                            <span className="dyna-chat-option-label">{opt}</span>
                            <span className="dyna-chat-option-arrow" aria-hidden="true">›</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Typing indicator */}
            {isTyping && (
              <div className="dyna-chat-msg assistant">
                <div className="dyna-chat-bot-avatar">
                  <BotAvatar size={14} />
                </div>
                <div className="dyna-chat-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Frozen banner — shown after inquiry submission */}
          {isFrozen && (
            <div className="dyna-chat-frozen-banner">
              <span>Enquiry submitted successfully!</span>
              <button className="dyna-chat-new-btn" onClick={startNewConversation}>
                Start new chat
              </button>
            </div>
          )}

          {/* Input */}
          <div className="dyna-chat-input-area">
            <input
              ref={inputRef}
              type="text"
              className="dyna-chat-input"
              placeholder={isFrozen ? 'Conversation complete' : 'Type your message...'}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={300}
              disabled={isTyping || isFrozen}
              autoComplete="off"
            />
            <button
              className="dyna-chat-send"
              onClick={() => sendMessage()}
              disabled={!input.trim() || isTyping || isFrozen}
              aria-label="Send"
            >
              <IconSend />
            </button>
          </div>

          <div className="dyna-chat-footer">Powered by Dynalog AI</div>
        </div>
      )}
    </>
  );
}
